import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faRotate } from '@fortawesome/free-solid-svg-icons';
import '../styles/Syncing.css';

const MESSAGES = [
  'Connecting to Strava…',
  'Syncing your workouts…',
  'Calculating points…',
  'Setting up your duel…',
  'Almost ready…',
];

interface Props {
  onDone: () => void;
}

export default function SyncingScreen({ onDone }: Props) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'syncing' | 'done'>('syncing');
  const timerRef = useRef<number | null>(null);
  const minTimeRef = useRef<number>(Date.now());

  // Cycle through messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex(i => (i + 1) % MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Animate progress bar (slow, then speed up near end)
  useEffect(() => {
    const tick = () => {
      setProgress(prev => {
        // Slow start, then gradually faster, cap at 92% until done
        const remaining = 100 - prev;
        const speed = prev < 30 ? 0.4 : prev < 60 ? 0.8 : prev < 85 ? 1.5 : 0.3;
        const next = Math.min(prev + speed, 92);
        return next;
      });
    };
    timerRef.current = window.setInterval(tick, 50);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // Listen for custom 'sync-done' event from AuthContext
  useEffect(() => {
    const handler = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      setProgress(100);
      setPhase('done');
      setTimeout(onDone, 800);
    };
    window.addEventListener('sync-done', handler);
    return () => window.removeEventListener('sync-done', handler);
  }, [onDone]);

  // Safety: after 30s, force done
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (phase === 'syncing') {
        if (timerRef.current) clearInterval(timerRef.current);
        setProgress(100);
        setPhase('done');
        setTimeout(onDone, 600);
      }
    }, 30000);
    return () => clearTimeout(timeout);
  }, [phase, onDone]);

  return (
    <div className="syncing-screen">
      <div className="syncing-content">
        <div className="syncing-logo">NoWeakWeek</div>

        <div className="syncing-icon">
          {phase === 'done' ? (
            <FontAwesomeIcon icon={faCheck} className="syncing-icon-check" />
          ) : (
            <FontAwesomeIcon icon={faRotate} className="syncing-icon-spin" />
          )}
        </div>

        <div className="syncing-msg">{phase === 'done' ? 'Ready!' : MESSAGES[msgIndex]}</div>

        <div className="syncing-bar-wrap">
          <div
            className="syncing-bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="syncing-hint">This usually takes a few seconds</div>
      </div>
    </div>
  );
}
